import React, { useState, useRef, useEffect } from 'react';
import { Conversation, ChatMessage, UserProfile, Attachment } from '../types';
import { InChatAIAssistant } from './InChatAIAssistant';
import { executeWateenAIAssist } from '../lib/gemini';
import {
  Phone,
  Video,
  Search,
  MoreVertical,
  Smile,
  Paperclip,
  Mic,
  Send,
  Sparkles,
  Lock,
  Unlock,
  Check,
  CheckCheck,
  Pin,
  Reply,
  Copy,
  Trash2,
  Bookmark,
  Volume2,
  Bot,
  Play,
  Pause,
  Clock,
  Heart,
  Flame,
  ThumbsUp,
  X,
  ChevronDown,
  Info,
  ArrowRight,
  Crown,
  Image as ImageIcon,
  Eraser,
  Ban,
  ShieldCheck,
} from 'lucide-react';
import { soundEffects } from '../lib/soundEffects';
import { ConfirmationModal, ConfirmationType } from './ConfirmationModal';

interface ChatViewProps {
  conversation: Conversation;
  messages: ChatMessage[];
  currentUser: UserProfile;
  onSendMessage: (msgData: Partial<ChatMessage>) => void;
  onEditMessage?: (msgId: string, newText: string) => void;
  onDeleteMessage?: (msgId: string, forEveryone: boolean) => void;
  onReactMessage: (msgId: string, emoji: string) => void;
  onPinMessage?: (msgId: string) => void;
  onStartCall: (type: 'voice' | 'video') => void;
  onOpenProfile: (username: string) => void;
  onSaveToVault?: (title: string, content: string, type: 'message' | 'media' | 'note') => void;
  onBackMobile?: () => void;
  isPartnerTyping?: boolean;
  partnerOnline?: boolean;
  onTyping?: (isTyping: boolean) => void;
  onUpdateProfile?: (profileData: Partial<UserProfile>) => Promise<void>;
  onDeleteConversation?: (convId: string) => void;
  onClearConversation?: (convId: string) => void;
  onBlockUser?: (targetUsername: string) => void;
  onUnblockUser?: (targetUsername: string) => void;
}

const MOODS: Array<{ key: ChatMessage['mood']; emoji: string; label: string }> = [
  { key: 'happy', emoji: '😄', label: 'سعيد' },
  { key: 'excited', emoji: '⚡', label: 'متحمس' },
  { key: 'love', emoji: '❤️', label: 'محبة' },
  { key: 'calm', emoji: '🌿', label: 'هادئ' },
  { key: 'sad', emoji: '🌧️', label: 'حزين' },
  { key: 'funny', emoji: '🎭', label: 'مرح' },
];

const QUICK_REACTIONS = ['❤️', '🔥', '👏', '😂', '👍', '⚡'];

export const ChatView: React.FC<ChatViewProps> = ({
  conversation,
  messages,
  currentUser,
  onSendMessage,
  onDeleteMessage,
  onReactMessage,
  onPinMessage,
  onStartCall,
  onOpenProfile,
  onSaveToVault,
  onBackMobile,
  isPartnerTyping,
  partnerOnline,
  onTyping,
  onUpdateProfile,
  onDeleteConversation,
  onClearConversation,
  onBlockUser,
  onUnblockUser,
}) => {
  const [inputText, setInputText] = useState('');
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [activeConfirmation, setActiveConfirmation] = useState<{
    isOpen: boolean;
    type: ConfirmationType;
    title: string;
    description: string;
    confirmText?: string;
    isDestructive?: boolean;
    action: () => void;
  } | null>(null);
  const typingTimeoutRef = useRef<any>(null);

  const handleInputUpdate = (text: string) => {
    setInputText(text);
    if (onTyping) {
      onTyping(true);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        onTyping(false);
      }, 2000);
    }
  };
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);

  // Futuristic features state
  const [isSecretReveal, setIsSecretReveal] = useState(false);
  const [selectedMood, setSelectedMood] = useState<ChatMessage['mood'] | undefined>(undefined);
  const [showMoodPicker, setShowMoodPicker] = useState(false);
  const [showAIReplies, setShowAIReplies] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<string[]>([]);
  const [revealedMessageIds, setRevealedMessageIds] = useState<Record<string, boolean>>({});
  const [showInChatAI, setShowInChatAI] = useState(false);
  const [inChatAITab, setInChatAITab] = useState<'improve' | 'tone' | 'reply' | 'translate' | 'summarize'>('improve');

  // Audio Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordDuration, setRecordDuration] = useState(0);
  const recordTimerRef = useRef<any>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const isRecordingCancelledRef = useRef(false);
  const recordDurationRef = useRef(0);

  // Search within chat
  const [isSearchingInChat, setIsSearchingInChat] = useState(false);
  const [chatSearchQuery, setChatSearchQuery] = useState('');

  // Audio player state
  const [playingAudioId, setPlayingAudioId] = useState<string | null>(null);
  const [playbackSpeed, setPlaybackSpeed] = useState<1 | 1.5 | 2>(1);
  const audioElementsRef = useRef<Record<string, HTMLAudioElement>>({});

  // Auto Reply State
  const autoReplyConfig = currentUser.autoReplies?.[conversation.id] || { enabled: false, customMessage: '' };
  const [showAutoReplySettings, setShowAutoReplySettings] = useState(false);
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(autoReplyConfig.enabled);
  const [autoReplyMessage, setAutoReplyMessage] = useState(autoReplyConfig.customMessage || '');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const wallpaperInputRef = useRef<HTMLInputElement>(null);

  const otherUsername = conversation.participants.find((p) => p !== currentUser.username) || conversation.participants[0];
  const isOtherUserBlocked = Boolean(
    conversation.type === 'direct' && otherUsername && currentUser.blockedUsers?.includes(otherUsername)
  );

  const handlePromptClearChat = () => {
    soundEffects.playTap();
    setShowOptionsMenu(false);
    setActiveConfirmation({
      isOpen: true,
      type: 'clear_chat',
      title: 'مسح سجل الدردشة',
      description: `هل أنت متأكد من مسح جميع الرسائل والوسائط في هذه المحادثة؟`,
      confirmText: 'نعم، امسح الدردشة',
      isDestructive: true,
      action: () => {
        onClearConversation?.(conversation.id);
        setActiveConfirmation(null);
      },
    });
  };

  const handlePromptDeleteChat = () => {
    soundEffects.playTap();
    setShowOptionsMenu(false);
    setActiveConfirmation({
      isOpen: true,
      type: 'delete_conversation',
      title: 'حذف المحادثة نهائياً',
      description: `هل أنت متأكد من رغبتك في حذف هذه المحادثة بالكامل وإزالتها من قائمة الدردشات؟`,
      confirmText: 'نعم، احذف المحادثة',
      isDestructive: true,
      action: () => {
        onDeleteConversation?.(conversation.id);
        setActiveConfirmation(null);
      },
    });
  };

  const handlePromptToggleBlock = () => {
    soundEffects.playTap();
    setShowOptionsMenu(false);
    if (!otherUsername) return;

    if (isOtherUserBlocked) {
      setActiveConfirmation({
        isOpen: true,
        type: 'unblock_user',
        title: 'إلغاء حظر المستخدم',
        description: `هل ترغب في إلغاء حظر @${otherUsername}؟ سيتمكن من مراسلتك والاتصال بك مجدداً.`,
        confirmText: 'نعم، فك الحظر',
        isDestructive: false,
        action: () => {
          onUnblockUser?.(otherUsername);
          setActiveConfirmation(null);
        },
      });
    } else {
      setActiveConfirmation({
        isOpen: true,
        type: 'block_user',
        title: 'حظر المستخدم',
        description: `هل أنت متأكد من رغبتك في حظر @${otherUsername}؟ لن يتمكن من إرسال رسائل إليك أو الاتصال بك.`,
        confirmText: 'نعم، احظر الشخص',
        isDestructive: true,
        action: () => {
          onBlockUser?.(otherUsername);
          setActiveConfirmation(null);
        },
      });
    }
  };

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isPartnerTyping]);

  // Mark as read when messages change
  useEffect(() => {
    // If current user disabled read receipts, do not send read receipts
    if (currentUser.privacy?.readReceipts === false) return;

    const unreadMessages = messages.filter(
      (m) => m.senderUsername !== currentUser.username && m.status !== 'read'
    );
    if (unreadMessages.length > 0) {
      fetch(`/api/conversations/${conversation.id}/read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: currentUser.username }),
      }).catch(() => {});
    }
  }, [messages, conversation.id, currentUser.username, currentUser.privacy?.readReceipts]);

  // Listen for Escape key to close conversation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onBackMobile) {
        onBackMobile();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onBackMobile]);

  // Handle Send
  const handleSend = () => {
    if (!inputText.trim()) return;

    onSendMessage({
      text: inputText.trim(),
      replyTo: replyingTo
        ? {
            id: replyingTo.id,
            senderName: replyingTo.senderName,
            senderUsername: replyingTo.senderUsername,
            text: replyingTo.text,
          }
        : undefined,
      isSecretReveal,
      mood: selectedMood,
    });

    soundEffects.playSent();
    setInputText('');
    if (onTyping) {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      onTyping(false);
    }
    setReplyingTo(null);
    setIsSecretReveal(false);
    setSelectedMood(undefined);
    setShowAIReplies(false);
  };

  // Voice recording simulation & real recording
  const startRecording = async () => {
    isRecordingCancelledRef.current = false;
    recordDurationRef.current = 0;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        
        if (isRecordingCancelledRef.current) return;

        const audioBlob = new Blob(audioChunksRef.current, { type: recorder.mimeType || undefined });
        const reader = new FileReader();
        reader.readAsDataURL(audioBlob);
        reader.onloadend = () => {
          const base64Data = reader.result as string;
          // Send voice message
          onSendMessage({
            text: '🎤 رسالة صوتية',
            attachments: [
              {
                id: `att_${Date.now()}`,
                type: 'audio',
                url: base64Data,
                name: 'رسالة صوتية',
                duration: recordDurationRef.current,
              },
            ],
          });
          soundEffects.playSent();
        };
      };

      recorder.start();
      setIsRecording(true);
      setRecordDuration(0);
      recordTimerRef.current = setInterval(() => {
        setRecordDuration((prev) => {
          const n = prev + 1;
          recordDurationRef.current = n;
          return n;
        });
      }, 1000);
    } catch {
      // Fallback voice simulation if mic permission denied
      setIsRecording(true);
      setRecordDuration(0);
      recordTimerRef.current = setInterval(() => {
        setRecordDuration((prev) => {
          const n = prev + 1;
          recordDurationRef.current = n;
          return n;
        });
      }, 1000);
    }
  };

  const stopRecording = (cancel: boolean = false) => {
    isRecordingCancelledRef.current = cancel;
    if (recordTimerRef.current) clearInterval(recordTimerRef.current);

    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    } else if (!cancel) {
      // Send simulated audio if real stream wasn't available
      onSendMessage({
        text: '🎤 رسالة صوتية مشفرة',
        attachments: [
          {
            id: `att_${Date.now()}`,
            type: 'audio',
            url: 'https://cdn.pixabay.com/download/audio/2022/03/15/audio_2484c0f862.mp3?filename=notification-bell-100257.mp3',
            name: 'مقطع صوتي.mp3',
            duration: recordDurationRef.current || 5,
          },
        ],
      });
      soundEffects.playSent();
    }
    setIsRecording(false);
    setRecordDuration(0);
  };

  // Image / File Upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check size limit: 25MB for standard free, 500MB for Premium
    const maxLimitBytes = currentUser.isPremium ? 500 * 1024 * 1024 : 25 * 1024 * 1024;
    if (file.size > maxLimitBytes) {
      if (!currentUser.isPremium) {
        alert('حجم الملف يتجاوز الحد المسموح للحساب المجاني (25MB). اشترك في وتين Premium 👑 لرفع ملفات حتى 500MB بدون ضغط!');
      } else {
        alert('حجم الملف يتجاوز الحد الأقصى المسموح (500MB).');
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result as string;
      try {
        const res = await fetch('/api/upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: currentUser.username,
            fileName: file.name,
            fileType: file.type,
            dataBase64: base64,
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          alert(data.error || 'تعذر رفع الملف');
          return;
        }
        if (data.url) {
          onSendMessage({
            text: file.type.startsWith('image') ? '📷 صورة مرفقة (HD)' : `📎 ${file.name}`,
            attachments: [
              {
                id: `att_${Date.now()}`,
                type: file.type.startsWith('image') ? 'image' : 'file',
                url: data.url,
                name: file.name,
                size: file.size,
              },
            ],
          });
          soundEffects.playSent();
        }
      } catch {
        // Direct local preview fallback
        onSendMessage({
          text: file.type.startsWith('image') ? '📷 صورة مرفقة' : `📎 ${file.name}`,
          attachments: [
            {
              id: `att_${Date.now()}`,
              type: file.type.startsWith('image') ? 'image' : 'file',
              url: base64,
              name: file.name,
              size: file.size,
            },
          ],
        });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleWallpaperUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Same size checks as standard upload for simplicity
    const maxLimitBytes = currentUser.isPremium ? 500 * 1024 * 1024 : 25 * 1024 * 1024;
    if (file.size > maxLimitBytes) {
      alert('حجم الصورة يتجاوز الحد المسموح.');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result as string;
      try {
        const res = await fetch('/api/upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: currentUser.username,
            fileName: 'wallpaper_' + file.name,
            fileType: file.type,
            fileData: base64,
          }),
        });
        const data = await res.json();
        
        if (data.url && onUpdateProfile) {
          await onUpdateProfile({
            chatWallpapers: {
              ...(currentUser.chatWallpapers || {}),
              [conversation.id]: data.url,
            }
          });
          soundEffects.playTap();
        }
      } catch (err) {
        console.error('Failed to upload wallpaper', err);
      }
    };
    reader.readAsDataURL(file);
  };

  // AI Smart Replies
  const fetchAISuggestions = async () => {
    setShowAIReplies(true);
    const lastIncoming = [...messages].reverse().find((m) => m.senderUsername !== currentUser.username);
    const targetText = lastIncoming?.text || 'مرحباً';

    try {
      const rawResult = await executeWateenAIAssist({
        task: 'suggest_replies',
        text: targetText,
        username: currentUser.username,
      });

      if (rawResult) {
        try {
          const cleanJson = rawResult.replace(/```json|```/g, '').trim();
          const parsed = JSON.parse(cleanJson);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setAiSuggestions(parsed);
            return;
          }
        } catch {
          // If non-JSON text, split or use default
        }
      }
      setAiSuggestions(['بالتأكيد، يسعدني ذلك! ✨', 'عمل متقن ورائع جداً 👏', 'سأتولى الأمر فوراً 👍']);
    } catch {
      setAiSuggestions(['بالتأكيد، يسعدني ذلك! ✨', 'عمل متقن ورائع جداً 👏', 'سأتولى الأمر فوراً 👍']);
    }
  };

  // Text to Speech
  const handleReadAloud = (text: string) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'ar-SA';
      window.speechSynthesis.speak(utterance);
    }
  };

  // Pinned message banner
  const pinnedMessages = messages.filter((m) => m.isPinned);

  const handleSaveAutoReply = async (enabled: boolean) => {
    setAutoReplyEnabled(enabled);
    try {
      const res = await fetch(`/api/conversations/${conversation.id}/auto-reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: currentUser.username,
          enabled,
          customMessage: autoReplyMessage,
        }),
      });
      if (!res.ok) {
        // Revert on error
        setAutoReplyEnabled(!enabled);
      } else {
        const data = await res.json();
        // Update local object without triggering full reload if possible
        if (!currentUser.autoReplies) currentUser.autoReplies = {};
        currentUser.autoReplies[conversation.id] = { enabled, customMessage: autoReplyMessage };
      }
    } catch {
      setAutoReplyEnabled(!enabled);
    }
  };

  // Determine Chat Theme
  const chatTheme = currentUser?.chatSettings?.theme || 'dark';
  let chatBgClass = 'bg-[#060911] text-slate-100'; // Default dark
  let headerBgClass = 'bg-neutral-900/95 border-slate-800/80';
  let inputBarBgClass = 'bg-[#080d16] border-slate-800/60';
  let bottomBarBgClass = 'bg-[#0a0f1b] border-slate-800/80';
  
  if (chatTheme === 'light') {
    chatBgClass = 'bg-[#f8fafc] text-slate-800';
    headerBgClass = 'bg-white/95 border-slate-200';
    inputBarBgClass = 'bg-slate-50 border-slate-200';
    bottomBarBgClass = 'bg-white border-slate-200';
  } else if (chatTheme === 'dim') {
    chatBgClass = 'bg-[#15202b] text-slate-200';
    headerBgClass = 'bg-[#192734]/95 border-slate-700/80';
    inputBarBgClass = 'bg-[#192734] border-slate-700/80';
    bottomBarBgClass = 'bg-[#15202b] border-slate-700/80';
  }

  // Get custom wallpaper for this conversation, if any
  const customWallpaper = currentUser?.chatWallpapers?.[conversation.id];
  const chatStyle = customWallpaper ? {
    backgroundImage: `url(${customWallpaper})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
  } : {};

  return (
    <div 
      className={`flex flex-col h-full font-['Cairo'] relative select-none ${!customWallpaper ? chatBgClass : 'text-white'}`}
      style={chatStyle}
    >
      {/* Dark overlay for better readability if custom wallpaper is set */}
      {customWallpaper && <div className="absolute inset-0 bg-black/40 z-0 pointer-events-none" />}
      
      {/* HEADER */}
      <div className={`sticky top-0 z-50 backdrop-blur-xl border-b h-16 px-3 sm:px-4 flex items-center justify-between shrink-0 gap-2 ${headerBgClass}`}>
        {/* User Info & Back navigation */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          {onBackMobile && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                soundEffects.playTap();
                onBackMobile();
              }}
              className="h-10 px-3 rounded-2xl bg-slate-800/60 hover:bg-slate-700/70 active:scale-95 border border-slate-700/60 hover:border-emerald-500/40 text-slate-200 hover:text-emerald-300 transition-all flex items-center gap-1.5 shrink-0 shadow-sm group"
              title="الرجوع إلى قائمة المحادثات"
              aria-label="الرجوع إلى المحادثات"
            >
              <ArrowRight className="w-4 h-4 text-emerald-400 group-hover:-translate-x-0.5 transition-transform" />
              <span className="text-xs font-bold text-slate-200 group-hover:text-white">رجوع</span>
            </button>
          )}

          <button
            onClick={() => onOpenProfile(otherUsername)}
            className="flex items-center gap-2.5 text-right group min-w-0 flex-1 hover:opacity-95 transition-opacity"
            title="عرض الملف الشخصي"
          >
            <div className="relative shrink-0">
              <img
                src={conversation.avatar}
                alt={conversation.title}
                className="w-10 h-10 rounded-2xl object-cover ring-2 ring-emerald-500/30 group-hover:ring-emerald-400/80 transition-all shadow-sm"
              />
              {partnerOnline && (
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-[#0e1422] shadow-sm shadow-emerald-500/50" />
              )}
            </div>

            <div className="min-w-0 flex-1 truncate">
              <div className="flex items-center gap-1.5">
                <span className="text-xs sm:text-sm font-bold text-white group-hover:text-emerald-300 transition truncate">
                  {conversation.title}
                </span>
                {conversation.type === 'ai' && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 font-mono">
                    AI
                  </span>
                )}
              </div>
              <div className="text-[11px] text-slate-400 flex items-center gap-1.5 truncate">
                {isPartnerTyping ? (
                  <span className="text-emerald-400 font-bold animate-pulse flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping inline-block" />
                    يكتب الآن...
                  </span>
                ) : partnerOnline ? (
                  <span className="text-emerald-400 font-medium flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
                    متصل الآن
                  </span>
                ) : (
                  <span className="text-slate-400 font-sans">@{otherUsername}</span>
                )}
              </div>
            </div>
          </button>
        </div>

        {/* Hidden Wallpaper File Input */}
        <input
          type="file"
          ref={wallpaperInputRef}
          onChange={handleWallpaperUpload}
          className="hidden"
          accept="image/*"
        />

        {/* Action Controls */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {conversation.type === 'direct' && (
            <>
              {/* Voice Call */}
              <button
                onClick={() => onStartCall('voice')}
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-white/[0.04] hover:bg-emerald-500/15 active:scale-95 border border-white/10 hover:border-emerald-500/40 text-slate-300 hover:text-emerald-400 transition-all flex items-center justify-center shadow-sm group"
                title="مكالمة صوتية فائقة الوضوح"
              >
                <Phone className="w-4 h-4 group-hover:scale-110 transition-transform" />
              </button>

              {/* Video Call */}
              <button
                onClick={() => onStartCall('video')}
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-white/[0.04] hover:bg-emerald-500/15 active:scale-95 border border-white/10 hover:border-emerald-500/40 text-slate-300 hover:text-emerald-400 transition-all flex items-center justify-center shadow-sm group"
                title="مكالمة فيديو عالية الدقة"
              >
                <Video className="w-4 h-4 group-hover:scale-110 transition-transform" />
              </button>
            </>
          )}

          {/* Search inside chat */}
          <button
            onClick={() => setIsSearchingInChat(!isSearchingInChat)}
            className={`w-9 h-9 sm:w-10 sm:h-10 rounded-2xl border transition-all flex items-center justify-center shadow-sm active:scale-95 group ${
              isSearchingInChat
                ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300'
                : 'bg-white/[0.04] hover:bg-white/[0.08] border-white/10 hover:border-white/20 text-slate-300 hover:text-white'
            }`}
            title="بحث في المحادثة"
          >
            <Search className="w-4 h-4 group-hover:scale-110 transition-transform" />
          </button>

          {/* AI Smart Summary (desktop pill) */}
          <button
            onClick={() => {
              setInChatAITab('summarize');
              setShowInChatAI(true);
            }}
            className="hidden md:flex items-center gap-1.5 px-3 py-2 rounded-2xl bg-gradient-to-r from-indigo-500/10 to-teal-500/10 hover:from-indigo-500/20 hover:to-teal-500/20 border border-indigo-500/25 hover:border-indigo-400/40 text-indigo-300 text-xs font-bold transition-all shadow-sm active:scale-95"
            title="تلخيص ذكي للمحادثة عبر الذكاء الاصطناعي ✨"
          >
            <Sparkles className="w-3.5 h-3.5 text-indigo-400 animate-pulse" />
            <span>تلخيص AI</span>
          </button>

          {/* More Options Dropdown Menu */}
          <div className="relative">
            <button
              onClick={() => setShowOptionsMenu(!showOptionsMenu)}
              className={`w-9 h-9 sm:w-10 sm:h-10 rounded-2xl border transition-all flex items-center justify-center shadow-sm active:scale-95 ${
                showOptionsMenu
                  ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300'
                  : 'bg-white/[0.04] hover:bg-white/[0.08] border-white/10 hover:border-white/20 text-slate-300 hover:text-white'
              }`}
              title="المزيد من الخيارات"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {showOptionsMenu && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowOptionsMenu(false)}
                />
                <div
                  className="absolute left-0 mt-2 w-64 bg-[#0d131f]/95 backdrop-blur-xl border border-slate-700/80 rounded-2xl p-2 shadow-2xl z-50 animate-in fade-in zoom-in-95 duration-150 space-y-1 text-right divide-y divide-slate-800/60"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* General actions */}
                  <div className="space-y-1 pb-1">
                    <button
                      onClick={() => {
                        setShowOptionsMenu(false);
                        onOpenProfile(otherUsername);
                      }}
                      className="w-full px-3 py-2 rounded-xl flex items-center justify-between text-xs font-semibold text-slate-200 hover:bg-slate-800/60 hover:text-white transition group"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 group-hover:scale-105 transition-transform">
                          <Info className="w-3.5 h-3.5" />
                        </div>
                        <span>معلومات وتفاصيل الحساب</span>
                      </div>
                    </button>

                    {conversation.type === 'direct' && (
                      <>
                        <button
                          onClick={() => {
                            setShowOptionsMenu(false);
                            wallpaperInputRef.current?.click();
                          }}
                          className="w-full px-3 py-2 rounded-xl flex items-center justify-between text-xs font-semibold text-slate-200 hover:bg-slate-800/60 hover:text-white transition group"
                        >
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 group-hover:scale-105 transition-transform">
                              <ImageIcon className="w-3.5 h-3.5" />
                            </div>
                            <span>تخصيص خلفية الدردشة</span>
                          </div>
                        </button>

                        <button
                          onClick={() => {
                            setShowOptionsMenu(false);
                            setShowAutoReplySettings(true);
                          }}
                          className="w-full px-3 py-2 rounded-xl flex items-center justify-between text-xs font-semibold text-slate-200 hover:bg-slate-800/60 hover:text-white transition group"
                        >
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-lg bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-400 group-hover:scale-105 transition-transform">
                              <Bot className="w-3.5 h-3.5" />
                            </div>
                            <span>الرد التلقائي الذكي (AI)</span>
                          </div>
                          {autoReplyEnabled && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-teal-500/20 text-teal-300 font-bold border border-teal-500/30">
                              مفعّل
                            </span>
                          )}
                        </button>
                      </>
                    )}

                    <button
                      onClick={() => {
                        setShowOptionsMenu(false);
                        setInChatAITab('summarize');
                        setShowInChatAI(true);
                      }}
                      className="w-full px-3 py-2 rounded-xl flex items-center justify-between text-xs font-semibold text-slate-200 hover:bg-slate-800/60 hover:text-white transition group md:hidden"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 group-hover:scale-105 transition-transform">
                          <Sparkles className="w-3.5 h-3.5" />
                        </div>
                        <span>تلخيص المحادثة عبر AI</span>
                      </div>
                    </button>
                  </div>

                  {/* Moderation & destructive actions */}
                  <div className="space-y-1 pt-1.5">
                    <button
                      onClick={() => {
                        setShowOptionsMenu(false);
                        handlePromptClearChat();
                      }}
                      className="w-full px-3 py-2 rounded-xl flex items-center justify-between text-xs font-semibold text-amber-300 hover:bg-amber-500/10 transition group"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 group-hover:scale-105 transition-transform">
                          <Eraser className="w-3.5 h-3.5" />
                        </div>
                        <span>مسح سجل الرسائل</span>
                      </div>
                    </button>

                    <button
                      onClick={() => {
                        setShowOptionsMenu(false);
                        handlePromptDeleteChat();
                      }}
                      className="w-full px-3 py-2 rounded-xl flex items-center justify-between text-xs font-semibold text-rose-400 hover:bg-rose-500/10 transition group"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-400 group-hover:scale-105 transition-transform">
                          <Trash2 className="w-3.5 h-3.5" />
                        </div>
                        <span>حذف المحادثة بالكامل</span>
                      </div>
                    </button>

                    {conversation.type === 'direct' && otherUsername && otherUsername !== currentUser.username && (
                      <button
                        onClick={() => {
                          setShowOptionsMenu(false);
                          handlePromptToggleBlock();
                        }}
                        className={`w-full px-3 py-2 rounded-xl flex items-center justify-between text-xs font-semibold transition group ${
                          isOtherUserBlocked
                            ? 'text-teal-300 hover:bg-teal-500/10'
                            : 'text-rose-400 hover:bg-rose-500/10'
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          <div
                            className={`w-7 h-7 rounded-lg border flex items-center justify-center group-hover:scale-105 transition-transform ${
                              isOtherUserBlocked
                                ? 'bg-teal-500/10 border-teal-500/20 text-teal-400'
                                : 'bg-rose-500/10 border-rose-500/20 text-rose-400'
                            }`}
                          >
                            {isOtherUserBlocked ? (
                              <ShieldCheck className="w-3.5 h-3.5" />
                            ) : (
                              <Ban className="w-3.5 h-3.5" />
                            )}
                          </div>
                          <span>{isOtherUserBlocked ? 'إلغاء حظر المستخدم' : 'حظر هذا المستخدم'}</span>
                        </div>
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modern Auto-Reply Modal */}
      {showAutoReplySettings && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setShowAutoReplySettings(false)}
        >
          <div
            className="w-full max-w-md bg-[#0f172a] border border-slate-700/80 rounded-3xl p-5 shadow-2xl space-y-4 animate-in zoom-in-95 duration-200 text-right"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-2xl bg-teal-500/15 border border-teal-500/30 flex items-center justify-center text-teal-400">
                  <Bot className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white">الرد التلقائي عبر الذكاء الاصطناعي</h3>
                  <p className="text-[11px] text-slate-400">وتين يجيب نيابة عنك عند عدم تواجدك</p>
                </div>
              </div>
              <button
                onClick={() => setShowAutoReplySettings(false)}
                className="w-8 h-8 rounded-xl bg-slate-800/60 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Toggle option */}
            <div className="flex items-center justify-between p-3 rounded-2xl bg-slate-800/40 border border-slate-700/50">
              <div>
                <span className="text-xs font-bold text-slate-200 block">تفعيل الرد التلقائي</span>
                <span className="text-[11px] text-slate-400">إرسال رد مخصص أو ذكي عند غيابك</span>
              </div>
              <button
                onClick={() => handleSaveAutoReply(!autoReplyEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  autoReplyEnabled ? 'bg-teal-500' : 'bg-slate-700'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    autoReplyEnabled ? 'translate-x-1' : '-translate-x-6'
                  }`}
                />
              </button>
            </div>

            {/* Custom text */}
            <div>
              <label className="text-xs font-medium text-slate-300 block mb-1.5">
                نص الرد التلقائي المخصص (اختياري):
              </label>
              <textarea
                value={autoReplyMessage}
                onChange={(e) => setAutoReplyMessage(e.target.value)}
                placeholder="أنا غير متواجد حالياً، وسيتولى وتين مساعدتك أو سأعاود مراسلتك قريباً..."
                className="w-full bg-[#141d2e] border border-slate-700/80 rounded-2xl p-3 text-xs text-white placeholder-slate-500 resize-none focus:outline-none focus:border-teal-500/60 transition h-24"
              />
            </div>

            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={() => {
                  handleSaveAutoReply(autoReplyEnabled);
                  setShowAutoReplySettings(false);
                }}
                className="flex-1 py-2.5 rounded-xl bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold text-xs shadow-lg shadow-teal-500/20 transition active:scale-95"
              >
                حفظ الإعدادات
              </button>
              <button
                onClick={() => setShowAutoReplySettings(false)}
                className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs transition"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Blocked Alert Banner */}
      {isOtherUserBlocked && (
        <div className="px-4 py-2 bg-rose-950/40 border-b border-rose-500/30 flex items-center justify-between text-xs text-rose-200">
          <div className="flex items-center gap-2">
            <Ban className="w-4 h-4 text-rose-400 shrink-0" />
            <span>المستخدم @{otherUsername} محظور حالياً. لن يتم استقبال رسائل أو اتصالات منه.</span>
          </div>
          <button
            onClick={handlePromptToggleBlock}
            className="px-2.5 py-1 rounded-xl bg-rose-500/20 hover:bg-rose-500/30 text-rose-200 border border-rose-500/40 text-[11px] font-bold transition shrink-0"
          >
            إلغاء الحظر
          </button>
        </div>
      )}

      {/* Pinned Messages Bar */}
      {pinnedMessages.length > 0 && (
        <div className="px-4 py-1.5 bg-[#101725] border-b border-slate-800/80 flex items-center justify-between text-xs text-slate-300">
          <div className="flex items-center gap-2 truncate">
            <Pin className="w-3.5 h-3.5 text-emerald-400 rotate-45 shrink-0" />
            <span className="text-[11px] font-semibold text-emerald-400 shrink-0">رسالة مثبتة:</span>
            <span className="text-[11px] truncate text-slate-300">{pinnedMessages[pinnedMessages.length - 1].text}</span>
          </div>
        </div>
      )}

      {/* Search in Chat Bar */}
      {isSearchingInChat && (
        <div className="p-2 bg-[#0e1422] border-b border-slate-800 flex items-center gap-2">
          <input
            type="text"
            value={chatSearchQuery}
            onChange={(e) => setChatSearchQuery(e.target.value)}
            placeholder="اكتب كلمة للبحث داخل المحادثة..."
            className="flex-1 px-3 py-1.5 bg-[#141c2a] border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
            autoFocus
          />
          <button
            onClick={() => {
              setIsSearchingInChat(false);
              setChatSearchQuery('');
            }}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* MESSAGES SCROLL VIEW */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 relative z-10">
        {messages
          .filter((m) => !chatSearchQuery || m.text.toLowerCase().includes(chatSearchQuery.toLowerCase()))
          .map((msg, index) => {
            const isMe = msg.senderUsername === currentUser.username;
            const isRevealed = revealedMessageIds[msg.id];

            return (
              <div
                key={`${msg.id || 'msg'}_${index}`}
                className={`flex flex-col ${isMe ? 'items-end' : 'items-start'} group/msg relative`}
              >
                {/* Quoted Reply context */}
                {msg.replyTo && (
                  <div className="text-[10px] text-slate-400 bg-black/40 px-3 py-1 rounded-t-xl border-r-2 border-emerald-400 max-w-sm truncate mb-0.5">
                    <span className="font-bold text-emerald-400">{msg.replyTo.senderName}: </span>
                    <span>{msg.replyTo.text}</span>
                  </div>
                )}

                {/* Message Bubble Container */}
                <div
                  className={`relative max-w-[85%] sm:max-w-md px-3.5 py-2.5 rounded-2xl shadow-md transition-all ${
                    isMe
                      ? currentUser.isPremium && currentUser.chatBubbleStyle === 'royal_gold'
                        ? 'bg-gradient-to-l from-amber-600 via-yellow-600 to-amber-700 text-slate-950 font-medium rounded-br-none shadow-amber-500/20 shadow-lg'
                        : currentUser.isPremium && currentUser.chatBubbleStyle === 'glass_morphism'
                        ? 'bg-indigo-950/80 border border-indigo-400/40 text-indigo-100 backdrop-blur-md rounded-br-none shadow-indigo-500/20 shadow-lg'
                        : currentUser.isPremium && currentUser.chatBubbleStyle === 'neon_glow'
                        ? 'bg-gradient-to-l from-emerald-500 to-teal-400 text-slate-950 font-bold rounded-br-none ring-1 ring-emerald-300 shadow-emerald-500/20 shadow-lg'
                        : currentUser.isPremium && currentUser.chatBubbleStyle === 'midnight_diamond'
                        ? 'bg-gradient-to-l from-slate-900 via-zinc-800 to-slate-900 text-white rounded-br-none border border-purple-400/40 shadow-xl'
                        : 'bg-gradient-to-l from-emerald-600 to-teal-700 text-white rounded-br-none'
                      : 'bg-[#121927] border border-slate-800/80 text-slate-100 rounded-bl-none'
                  }`}
                >
                  {/* Mood Tag */}
                  {msg.mood && (
                    <div className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-black/30 text-emerald-300 mb-1.5">
                      <span>{MOODS.find((m) => m.key === msg.mood)?.emoji}</span>
                      <span>{MOODS.find((m) => m.key === msg.mood)?.label}</span>
                    </div>
                  )}

                  {/* Secret Holographic Reveal Feature */}
                  {msg.isSecretReveal && !isRevealed ? (
                    <button
                      onClick={() => {
                        setRevealedMessageIds((prev) => ({ ...prev, [msg.id]: true }));
                        soundEffects.playTap();
                      }}
                      className="flex items-center gap-2.5 py-2 px-3 rounded-xl bg-black/40 border border-emerald-400/40 hover:border-emerald-400 backdrop-blur-md text-xs text-emerald-300 transition group/sec"
                    >
                      <Lock className="w-4 h-4 text-emerald-400 animate-pulse" />
                      <div className="text-right">
                        <div className="font-bold">رسالة وتين سرية 🔒</div>
                        <div className="text-[10px] text-slate-300">انقر هنا لكشف النص الهولوغرافي</div>
                      </div>
                    </button>
                  ) : (
                    <div>
                      {/* Image attachments */}
                      {msg.attachments?.map((att) => {
                        if (att.type === 'image') {
                          return (
                            <img
                              key={att.id}
                              src={att.url}
                              alt={att.name}
                              className="w-full max-h-72 object-cover rounded-xl mb-1.5"
                            />
                          );
                        }
                        if (att.type === 'audio') {
                          return (
                            <div
                              key={att.id}
                              className="flex items-center gap-3 p-2 rounded-xl bg-black/20 my-1 min-w-[220px]"
                            >
                              <button
                                onClick={() => {
                                  // toggle play
                                  if (playingAudioId === att.id) {
                                    audioElementsRef.current[att.id]?.pause();
                                    setPlayingAudioId(null);
                                  } else {
                                    const el = new Audio(att.url);
                                    el.playbackRate = playbackSpeed;
                                    el.play().catch(err => {
                                      console.error('Audio playback failed', err);
                                      setPlayingAudioId(null);
                                    });
                                    el.onended = () => setPlayingAudioId(null);
                                    audioElementsRef.current[att.id] = el;
                                    setPlayingAudioId(att.id);
                                  }
                                }}
                                className="w-8 h-8 rounded-full bg-emerald-500 text-[#080d16] flex items-center justify-center shadow"
                              >
                                {playingAudioId === att.id ? (
                                  <Pause className="w-4 h-4 fill-current" />
                                ) : (
                                  <Play className="w-4 h-4 fill-current ml-0.5" />
                                )}
                              </button>

                              {/* Interactive simulated waveform bars */}
                              <div className="flex-1 flex items-center gap-0.5 h-6">
                                {[40, 70, 30, 90, 60, 80, 50, 95, 40, 75, 60, 30, 85, 45].map((h, bIdx) => (
                                  <span
                                    key={bIdx}
                                    style={{ height: `${h}%` }}
                                    className={`w-1 rounded-full transition-all ${
                                      playingAudioId === att.id ? 'bg-emerald-300 animate-pulse' : 'bg-white/40'
                                    }`}
                                  />
                                ))}
                              </div>

                              {/* Speed toggle */}
                              <button
                                onClick={() => {
                                  const nextSpeed = playbackSpeed === 1 ? 1.5 : playbackSpeed === 1.5 ? 2 : 1;
                                  setPlaybackSpeed(nextSpeed);
                                  if (audioElementsRef.current[att.id]) {
                                    audioElementsRef.current[att.id].playbackRate = nextSpeed;
                                  }
                                }}
                                className="px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-white/20 text-white"
                              >
                                {playbackSpeed}x
                              </button>
                            </div>
                          );
                        }
                        return null;
                      })}

                      {/* Main Message Text */}
                      <p 
                        className="font-normal leading-relaxed whitespace-pre-wrap select-text"
                        style={{ fontSize: currentUser.chatSettings?.fontSize ? `${currentUser.chatSettings.fontSize}px` : undefined }}
                      >
                        {msg.text}
                      </p>
                    </div>
                  )}

                  {/* Bubble Footer: Time, Edited mark, Read ticks */}
                  <div className="flex items-center justify-end gap-1.5 mt-1 text-[10px] text-white/70 select-none">
                    {msg.isEdited && <span>(معدّلة)</span>}
                    <span>
                      {new Date(msg.timestamp).toLocaleTimeString('ar-SA', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                    {isMe && (
                      <span>
                        {msg.status === 'read' && currentUser.privacy?.readReceipts !== false ? (
                          <CheckCheck className="w-3.5 h-3.5 text-sky-400 inline" />
                        ) : msg.status === 'delivered' || msg.status === 'read' ? (
                          <CheckCheck className="w-3.5 h-3.5 text-white/80 inline" />
                        ) : (
                          <Check className="w-3.5 h-3.5 text-white/80 inline" />
                        )}
                      </span>
                    )}
                  </div>
                </div>

                {/* Reactions list under bubble */}
                {msg.reactions && msg.reactions.length > 0 && (
                  <div className="flex items-center gap-1 mt-1 -mb-1 px-1">
                    {msg.reactions.map((r, rIdx) => (
                      <button
                        key={rIdx}
                        onClick={() => onReactMessage(msg.id, r.emoji)}
                        className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#121927] border border-slate-800 text-xs shadow-sm hover:scale-110 transition"
                      >
                        <span>{r.emoji}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Quick Message Hover Action Bar */}
                <div
                  className={`opacity-0 group-hover/msg:opacity-100 transition-opacity absolute -top-3 ${
                    isMe ? 'left-2' : 'right-2'
                  } flex items-center gap-1 p-1 rounded-xl bg-[#0e1422] border border-slate-800 shadow-xl z-10`}
                >
                  {/* Quick Reactions */}
                  {QUICK_REACTIONS.slice(0, 3).map((emo) => (
                    <button
                      key={emo}
                      onClick={() => onReactMessage(msg.id, emo)}
                      className="p-1 hover:scale-125 transition text-xs"
                    >
                      {emo}
                    </button>
                  ))}

                  <button
                    onClick={() => setReplyingTo(msg)}
                    className="p-1 text-slate-400 hover:text-emerald-400"
                    title="رد"
                  >
                    <Reply className="w-3.5 h-3.5" />
                  </button>

                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(msg.text);
                      soundEffects.playTap();
                    }}
                    className="p-1 text-slate-400 hover:text-white"
                    title="نسخ"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>

                  <button
                    onClick={() => handleReadAloud(msg.text)}
                    className="p-1 text-slate-400 hover:text-indigo-400"
                    title="قراءة صوتية"
                  >
                    <Volume2 className="w-3.5 h-3.5" />
                  </button>

                  {onPinMessage && (
                    <button
                      onClick={() => onPinMessage(msg.id)}
                      className={`p-1 ${msg.isPinned ? 'text-emerald-400' : 'text-slate-400 hover:text-emerald-400'}`}
                      title="تثبيت الرسالة"
                    >
                      <Pin className="w-3.5 h-3.5" />
                    </button>
                  )}

                  {onSaveToVault && (
                    <button
                      onClick={() => onSaveToVault(`رسالة من ${msg.senderName}`, msg.text, 'message')}
                      className="p-1 text-slate-400 hover:text-amber-400"
                      title="حفظ في خزنة وتين"
                    >
                      <Bookmark className="w-3.5 h-3.5" />
                    </button>
                  )}

                  {isMe && onDeleteMessage && (
                    <button
                      onClick={() => onDeleteMessage(msg.id, true)}
                      className="p-1 text-slate-400 hover:text-rose-400"
                      title="حذف لدى الجميع"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        <div ref={messagesEndRef} />
      </div>

      {/* AI Smart Suggestions Tray */}
      {showAIReplies && (
        <div className="px-4 py-2 bg-[#0c121e] border-t border-slate-800/80 flex items-center gap-2 overflow-x-auto no-scrollbar relative z-10 shrink-0">
          <span className="text-[10px] font-bold text-indigo-400 flex items-center gap-1 shrink-0">
            <Sparkles className="w-3.5 h-3.5" />
            ردود وتين الذكية:
          </span>
          {aiSuggestions.map((sug, sIdx) => (
            <button
              key={sIdx}
              onClick={() => {
                setInputText(sug);
                setShowAIReplies(false);
              }}
              className="px-3 py-1 rounded-full bg-[#162030] hover:bg-emerald-950/40 border border-slate-700 hover:border-emerald-500/50 text-xs text-slate-200 hover:text-emerald-300 transition shrink-0"
            >
              {sug}
            </button>
          ))}
          <button
            onClick={() => setShowAIReplies(false)}
            className="p-1 text-slate-400 hover:text-white shrink-0"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Reply Preview Banner */}
      {replyingTo && (
        <div className="px-4 py-2 bg-[#0e1522] border-t border-slate-800 flex items-center justify-between relative z-10 shrink-0">
          <div className="flex items-center gap-2 truncate">
            <Reply className="w-4 h-4 text-emerald-400 shrink-0" />
            <div className="text-xs truncate">
              <span className="font-bold text-emerald-400">رد على {replyingTo.senderName}: </span>
              <span className="text-slate-400">{replyingTo.text}</span>
            </div>
          </div>
          <button
            onClick={() => setReplyingTo(null)}
            className="p-1 rounded-lg text-slate-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Futuristic Feature Quick Bar (Mood, Secret Reveal, AI Suggest) */}
      <div className={`px-4 py-1.5 border-t flex items-center justify-between text-xs ${inputBarBgClass}`}>
        <div className="flex items-center gap-2">
          {/* Secret Holographic Reveal toggle */}
          <button
            onClick={() => setIsSecretReveal(!isSecretReveal)}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition ${
              isSecretReveal
                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/50'
                : 'text-slate-400 hover:text-slate-200 bg-[#121927]'
            }`}
            title="إخفاء الرسالة خلف غشاء هولوغرافي سري يكشفه الطرف الآخر باللمس"
          >
            <Lock className="w-3 h-3" />
            <span>رسالة سرية</span>
          </button>

          {/* Mood Message toggle */}
          <div className="relative">
            <button
              onClick={() => setShowMoodPicker(!showMoodPicker)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition ${
                selectedMood
                  ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/50'
                  : 'text-slate-400 hover:text-slate-200 bg-[#121927]'
              }`}
            >
              <span>{selectedMood ? MOODS.find((m) => m.key === selectedMood)?.emoji : '🎭'}</span>
              <span>{selectedMood ? MOODS.find((m) => m.key === selectedMood)?.label : 'مزاج الرسالة'}</span>
            </button>

            {showMoodPicker && (
              <div className="absolute bottom-full mb-2 right-0 bg-[#0e1422] border border-slate-800 rounded-2xl p-2 shadow-2xl z-30 flex gap-1">
                {MOODS.map((m) => (
                  <button
                    key={m.key}
                    onClick={() => {
                      setSelectedMood(m.key);
                      setShowMoodPicker(false);
                    }}
                    className="p-1.5 rounded-xl hover:bg-white/10 text-base"
                    title={m.label}
                  >
                    {m.emoji}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* AI Smart Assistant & Quick Replies Buttons */}
        <div className="flex items-center gap-1.5">
          {currentUser.aiSettings?.enabled !== false && (
            <button
              onClick={() => {
                setInChatAITab('improve');
                setShowInChatAI(true);
              }}
              className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-gradient-to-r from-indigo-600/30 to-purple-600/30 hover:from-indigo-600/50 hover:to-purple-600/50 text-[11px] font-black text-indigo-300 border border-indigo-500/40 hover:border-indigo-400 transition shadow-sm"
              title="مساعد وتين الذكي (✨ AI): صياغة، تغيير أسلوب، ترجمة، اقتراح رد، وتلخيص"
            >
              <Sparkles className="w-3 h-3 text-indigo-400 animate-pulse" />
              <span>✨ AI</span>
            </button>
          )}
          {currentUser.aiSettings?.enabled !== false && currentUser.aiSettings?.smartReplies !== false && (
            <button
              onClick={fetchAISuggestions}
              className="flex items-center gap-1 text-[11px] font-bold text-slate-400 hover:text-indigo-300 transition"
            >
              <span>رد سريع</span>
            </button>
          )}
        </div>
      </div>

      {/* BOTTOM INPUT BAR */}
      <div className={`p-3 border-t flex items-center gap-2 relative z-10 shrink-0 ${bottomBarBgClass}`}>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          className="hidden"
          accept="image/*,.pdf,.doc,.docx,.zip"
        />

        {/* Attachment Button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          className="p-2.5 rounded-2xl bg-[#141b27] hover:bg-[#1c2637] border border-slate-800 text-slate-300 hover:text-white transition shrink-0"
          title="إرفاق صورة أو مستند"
        >
          <Paperclip className="w-4 h-4" />
        </button>

        {/* AI Quick Button in input bar */}
        <button
          onClick={() => {
            setInChatAITab('improve');
            setShowInChatAI(true);
          }}
          className="p-2.5 rounded-2xl bg-indigo-950/40 hover:bg-indigo-900/60 border border-indigo-500/40 text-indigo-300 hover:text-white transition shrink-0"
          title="افتح مساعد وتين الذكي ✨ AI"
        >
          <Sparkles className="w-4 h-4 text-indigo-400" />
        </button>

        {/* Audio Recording Active View */}
        {isRecording ? (
          <div className="flex-1 flex items-center justify-between px-4 py-2 bg-rose-950/40 border border-rose-500/40 rounded-2xl text-rose-300">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-ping" />
              <span className="text-xs font-mono font-bold">
                {Math.floor(recordDuration / 60)}:{(recordDuration % 60).toString().padStart(2, '0')}
              </span>
              <span className="text-xs">جاري التسجيل الصوتي...</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => stopRecording(true)}
                className="text-xs text-slate-400 hover:text-rose-400"
              >
                إلغاء
              </button>
              <button
                onClick={() => stopRecording(false)}
                className="px-3 py-1 rounded-xl bg-rose-500 text-white text-xs font-bold shadow"
              >
                إرسال
              </button>
            </div>
          </div>
        ) : (
          /* Text Input Field */
          <div className="flex-1 relative flex items-center">
            <textarea
              value={inputText}
              onChange={(e) => handleInputUpdate(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              rows={1}
              placeholder="اكتب رسالة على وتين... (أو اضغط ✨ AI للصياغة)"
              className="w-full pl-3 pr-4 py-2.5 bg-[#141b27] border border-slate-800 rounded-2xl text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition resize-none max-h-24"
            />
          </div>
        )}

        {/* Mic or Send Button */}
        {inputText.trim() ? (
          <button
            onClick={handleSend}
            className="p-2.5 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white shadow-lg shadow-emerald-500/20 transition active:scale-95 shrink-0"
            title="إرسال"
          >
            <Send className="w-4 h-4" />
          </button>
        ) : !isRecording ? (
          <button
            onClick={startRecording}
            className="p-2.5 rounded-2xl bg-[#141b27] hover:bg-emerald-950/40 border border-slate-800 hover:border-emerald-500/40 text-slate-300 hover:text-emerald-400 transition shrink-0"
            title="تسجيل رسالة صوتية"
          >
            <Mic className="w-4 h-4" />
          </button>
        ) : null}
      </div>

      {/* In-Chat Wateen AI Assistant Drawer */}
      {showInChatAI && (
        <InChatAIAssistant
          isOpen={showInChatAI}
          onClose={() => setShowInChatAI(false)}
          inputText={inputText}
          onApplyText={(newText) => setInputText(newText)}
          recentMessages={messages}
          currentUser={currentUser}
          otherParticipantName={conversation.title}
          defaultTab={inChatAITab}
        />
      )}

      {/* Active Confirmation Modal */}
      {activeConfirmation && (
        <ConfirmationModal
          isOpen={activeConfirmation.isOpen}
          type={activeConfirmation.type}
          title={activeConfirmation.title}
          description={activeConfirmation.description}
          confirmText={activeConfirmation.confirmText}
          isDestructive={activeConfirmation.isDestructive}
          onConfirm={activeConfirmation.action}
          onCancel={() => setActiveConfirmation(null)}
        />
      )}
    </div>
  );
};
